import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { AnalyticsEventPayload } from "@swiggy-private/analytics/dist/interfaces";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { ResponseContext } from "@swiggy-private/portal-dineout-context";
import { CrossSellContent } from "../components/CrossSellContent";
import { CROSS_SELL_ANIMATION_DURATION_MS } from "../components/CrossSellContent/constants";
import { buildDishMap, resolveCrossSellDisplayItems } from "../components/CrossSellContent/helpers";

export const useCrossSellRenderer = (
    onCrossSellShown?: (itemId: string) => void,
    impressionAnalytics?: (payload: Partial<AnalyticsEventPayload>) => void
) => {
    const { state } = useContext(ResponseContext);
    const dishMap = useMemo(
        () => buildDishMap(state.menu.initialData as IPLWidgetControllerCards[]),
        [state.menu.initialData]
    );

    const cartItemIds = useMemo(
        () => new Set(state.cart?.localCart?.cartItems?.map((i) => i.menu_item_id) ?? []),
        [state.cart?.localCart?.cartItems]
    );

    const crossSellActiveItemId = useMemo(() => {
        if (cartItemIds.size === 0) return null;
        const crossSellMap =
            state.crossSell?.config?.crossSell?.crossSellPerItem?.crossSellMap ?? {};
        return (
            state.crossSell?.entries?.find(
                (e) =>
                    e.isExpanded &&
                    cartItemIds.has(e.itemId) &&
                    (crossSellMap[e.itemId]?.rawChildItems?.length ?? 0) > 0
            )?.itemId ?? null
        );
    }, [state.crossSell?.entries, cartItemIds, state.crossSell?.config]);

    const [leavingItemId, setLeavingItemId] = useState<string | null>(null);
    const prevActiveIdRef = useRef<string | null>(null);
    // Ref mirrors leavingItemId for the effect guard — avoids adding leavingItemId to effect deps
    // which would restart the cleanup timer on the same tick it was set.
    const leavingItemIdRef = useRef<string | null>(null);

    // Calling setState during render is the React-supported way to derive state synchronously.
    // This ensures leavingItemId is set in the same render that activeId changes,
    // preventing an unmount gap that would break the CSS transition.
    if (prevActiveIdRef.current !== crossSellActiveItemId) {
        const prev = prevActiveIdRef.current;
        prevActiveIdRef.current = crossSellActiveItemId;
        if (prev !== null) {
            leavingItemIdRef.current = prev;
            setLeavingItemId(prev);
        }
    }

    useEffect(() => {
        if (!leavingItemIdRef.current) return;
        const timer = setTimeout(() => {
            leavingItemIdRef.current = null;
            setLeavingItemId(null);
        }, CROSS_SELL_ANIMATION_DURATION_MS);
        return () => clearTimeout(timer);
    }, [crossSellActiveItemId]);

    const getCrossSellRenderer = useCallback(
        (itemId: string): (() => ReactNode) | undefined => {
            const isActive = itemId === crossSellActiveItemId;
            const isLeaving = itemId === leavingItemId;
            if (!isActive && !isLeaving) return undefined;

            const cartItemIdsAtOpen = state.crossSell?.entries?.find(
                (e) => e.itemId === itemId
            )?.cartItemIdsAtOpen;

            const currentCartMenuItemIds =
                state.cart?.localCart?.cartItems?.map((line) => line.menu_item_id) ?? [];

            const displayItems = resolveCrossSellDisplayItems({
                itemId,
                dishMap,
                crossSellConfig: state.crossSell?.config,
                cartItemIdsAtOpen,
                currentCartMenuItemIds,
                preference: state.menu.preference ?? "",
                appliedFilters: state.menu.appliedFilters,
            });
            if (displayItems.length === 0) return undefined;
            const render = () => (
                <CrossSellContent
                    itemId={itemId}
                    isLeaving={!isActive}
                    onCrossSellShown={onCrossSellShown}
                    impressionAnalytics={impressionAnalytics}
                />
            );
            render.displayName = "CrossSellRenderer";
            return render;
        },
        [
            crossSellActiveItemId,
            leavingItemId,
            dishMap,
            state.crossSell,
            state.cart?.localCart?.cartItems,
            state.menu,
            onCrossSellShown,
            impressionAnalytics,
        ]
    );

    return { getCrossSellRenderer };
};
